import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import { callAuthenticatedFunction } from './userManagementApi';

const ROLE_OPTIONS = [
  'platform_admin',
  'owner',
  'instructor',
  'student',
  'member',
];

const TIER_OPTIONS = [
  'calendar',
  'calendar-logbook-techlog',
  'full-package',
];

export default function PlatformAdminDashboard() {
  const session = useSession();

  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    role: '',
    membership_tier: '',
    org_name: '',
    home_airfield: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [actionBusyUserId, setActionBusyUserId] = useState('');
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');

  async function loadDashboardData() {
    if (!session?.user?.email) {
      setErrorText('No logged-in user found.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorText('');

    const profileResult = await supabase
      .from('users')
      .select('*')
      .eq('email', session.user.email)
      .single();

    if (profileResult.error) {
      setErrorText(profileResult.error.message);
      setLoading(false);
      return;
    }

    setCurrentUserProfile(profileResult.data);

    if (profileResult.data?.role !== 'platform_admin') {
      setLoading(false);
      return;
    }

    const [usersResult, aircraftResult, bookingsResult] = await Promise.all([
      supabase.from('users').select('*').order('created_at', { ascending: false }),
      supabase.from('aircraft').select('*'),
      supabase.from('bookings').select('*'),
    ]);

    if (usersResult.error) {
      setErrorText(usersResult.error.message);
    } else {
      setUsers(usersResult.data || []);
    }

    if (!aircraftResult.error) {
      setAircraft(aircraftResult.data || []);
    }

    if (!bookingsResult.error) {
      setBookings(bookingsResult.data || []);
    }

    setLoading(false);
  }

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return users;

    return users.filter((user) => {
      const statusText = user.is_active === false ? 'suspended inactive' : 'active';

      const searchableText = [
        user.full_name,
        user.email,
        user.role,
        user.org_name,
        user.membership_tier,
        user.home_airfield,
        user.phone,
        statusText,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [users, searchTerm]);

  const stats = useMemo(() => {
    const byRole = {};
    const byTier = {};
    const byOrg = {};

    users.forEach((user) => {
      const role = user.role || 'Not set';
      const tier = user.membership_tier || 'Not set';
      const org = user.org_name || 'Not set';

      byRole[role] = (byRole[role] || 0) + 1;
      byTier[tier] = (byTier[tier] || 0) + 1;
      byOrg[org] = (byOrg[org] || 0) + 1;
    });

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const newUsersThisWeek = users.filter((user) => {
      if (!user.created_at) return false;
      return new Date(user.created_at) >= sevenDaysAgo;
    }).length;

    const activeUsers = users.filter((user) => user.is_active !== false).length;
    const suspendedUsers = users.filter((user) => user.is_active === false).length;

    return {
      totalUsers: users.length,
      activeUsers,
      suspendedUsers,
      totalAircraft: aircraft.length,
      totalBookings: bookings.length,
      totalOrganisations: Object.keys(byOrg).length,
      newUsersThisWeek,
      byRole,
      byTier,
      byOrg,
    };
  }, [users, aircraft, bookings]);

  function openEdit(user) {
    setMessageText('');
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || '',
      role: user.role || '',
      membership_tier: user.membership_tier || 'calendar',
      org_name: user.org_name || '',
      home_airfield: user.home_airfield || '',
      is_active: user.is_active !== false,
    });
  }

  function closeEdit() {
    setEditingUser(null);
    setEditForm({
      full_name: '',
      role: '',
      membership_tier: '',
      org_name: '',
      home_airfield: '',
      is_active: true,
    });
  }

  async function saveEdit() {
    if (!editingUser?.id) {
      setMessageText('Could not save: missing user ID.');
      return;
    }

    setSaving(true);
    setMessageText('');

    const updates = {
      full_name: editForm.full_name.trim() || null,
      role: editForm.role || null,
      membership_tier: editForm.membership_tier || 'calendar',
      org_name: editForm.org_name.trim() || null,
      home_airfield: editForm.home_airfield.trim() || null,
      is_active: editForm.is_active,
    };

    let result;
    try {
      result = await callAuthenticatedFunction(
        'manage-platform-user',
        { userId: editingUser.id, updates },
        'Could not save user changes.'
      );
    } catch (error) {
      setMessageText(`Save failed: ${error.message}`);
      setSaving(false);
      return;
    }

    setUsers((previousUsers) =>
      previousUsers.map((user) =>
        user.id === editingUser.id
          ? { ...user, ...updates, ...result.user }
          : user
      )
    );

    setSaving(false);
    setMessageText('User updated successfully.');
    closeEdit();
  }

  async function toggleUserActive(user) {
    if (!user?.id) {
      setMessageText('Could not update user: missing user ID.');
      return;
    }

    const currentlyActive = user.is_active !== false;
    const nextActiveState = !currentlyActive;
    const actionLabel = nextActiveState ? 'reactivate' : 'suspend';

    if (!nextActiveState) {
      const confirmed = window.confirm(
        `Suspend this user?\n\n${user.full_name || user.email || 'Selected user'}\n\nThey will remain in the database, but should be treated as inactive.`
      );

      if (!confirmed) return;
    }

    setActionBusyUserId(user.id);
    setMessageText('');

    let result;
    try {
      result = await callAuthenticatedFunction(
        'manage-platform-user',
        { userId: user.id, updates: { is_active: nextActiveState } },
        `Could not ${actionLabel} user.`
      );
    } catch (error) {
      setMessageText(`Could not ${actionLabel} user: ${error.message}`);
      setActionBusyUserId('');
      return;
    }

    setUsers((previousUsers) =>
      previousUsers.map((existingUser) =>
        existingUser.id === user.id
          ? { ...existingUser, ...result.user }
          : existingUser
      )
    );

    setActionBusyUserId('');
    setMessageText(nextActiveState ? 'User reactivated successfully.' : 'User suspended successfully.');
  }

  function StatBox({ title, value }) {
    return (
      <div style={styles.statBox}>
        <div style={styles.statValue}>{value}</div>
        <div style={styles.statTitle}>{title}</div>
      </div>
    );
  }

  function MiniList({ title, items }) {
    return (
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>{title}</h3>
        {Object.entries(items).length === 0 ? (
          <p style={styles.muted}>No data</p>
        ) : (
          Object.entries(items).map(([label, count]) => (
            <div key={label} style={styles.listRow}>
              <span>{label}</span>
              <strong>{count}</strong>
            </div>
          ))
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.muted}>Loading platform dashboard...</p>
      </div>
    );
  }

  if (errorText) {
    return (
      <div style={styles.page}>
        <div style={styles.errorBox}>Could not load dashboard data: {errorText}</div>
        <Link to="/dashboard" style={styles.backButton}>Back to Dashboard</Link>
      </div>
    );
  }

  if (currentUserProfile?.role !== 'platform_admin') {
    return (
      <div style={styles.page}>
        <div style={styles.errorBox}>
          Access denied. This dashboard is only available to Wingit platform administrators.
        </div>
        <Link to="/dashboard" style={styles.backButton}>Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h1 style={styles.title}>Wingit Platform Admin</h1>
          <p style={styles.subtitle}>Registrants, organisations, aircraft and booking overview.</p>
        </div>
        <Link to="/dashboard" style={styles.backButton}>Back to Dashboard</Link>
      </div>

      {messageText && (
        <div
          style={
            messageText.startsWith('Save failed') || messageText.startsWith('Could not')
              ? styles.errorBox
              : styles.successBox
          }
        >
          {messageText}
        </div>
      )}

      <div style={styles.statsGrid}>
        <StatBox title="Total users" value={stats.totalUsers} />
        <StatBox title="Active users" value={stats.activeUsers} />
        <StatBox title="Suspended users" value={stats.suspendedUsers} />
        <StatBox title="New users this week" value={stats.newUsersThisWeek} />
        <StatBox title="Organisations" value={stats.totalOrganisations} />
        <StatBox title="Aircraft" value={stats.totalAircraft} />
        <StatBox title="Bookings" value={stats.totalBookings} />
      </div>

      <div style={styles.gridThree}>
        <MiniList title="Users by role" items={stats.byRole} />
        <MiniList title="Users by tier" items={stats.byTier} />
        <MiniList title="Users by organisation" items={stats.byOrg} />
      </div>

      <div style={styles.card}>
        <div style={styles.tableHeader}>
          <div>
            <h3 style={styles.cardTitle}>Registrants</h3>
            <p style={styles.muted}>
              Showing {filteredUsers.length} of {users.length} users
            </p>
          </div>

          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={styles.searchInput}
          />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Actions</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Organisation</th>
                <th style={styles.th}>Tier</th>
                <th style={styles.th}>Home Airfield</th>
                <th style={styles.th}>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td style={styles.td} colSpan="9">
                    No users match your search.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isActive = user.is_active !== false;
                  const isBusy = actionBusyUserId === user.id;

                  return (
                    <tr key={user.id || user.email}>
                      <td style={styles.td}>
                        <div style={styles.actionRow}>
                          <button type="button" style={styles.smallButton} onClick={() => openEdit(user)}>
                            Edit
                          </button>

                          <button
                            type="button"
                            style={isActive ? styles.suspendButton : styles.reactivateButton}
                            onClick={() => toggleUserActive(user)}
                            disabled={isBusy}
                          >
                            {isBusy ? 'Working...' : isActive ? 'Suspend' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={isActive ? styles.activeBadge : styles.suspendedBadge}>
                          {isActive ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td style={styles.td}>{user.full_name || '-'}</td>
                      <td style={styles.td}>{user.email || '-'}</td>
                      <td style={styles.td}>{user.role || '-'}</td>
                      <td style={styles.td}>{user.org_name || '-'}</td>
                      <td style={styles.td}>{user.membership_tier || '-'}</td>
                      <td style={styles.td}>{user.home_airfield || '-'}</td>
                      <td style={styles.td}>
                        {user.created_at ? new Date(user.created_at).toLocaleString('en-GB') : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Edit User</h2>
            <p style={styles.muted}>{editingUser.email}</p>

            <label style={styles.label}>
              Full name
              <input
                type="text"
                value={editForm.full_name}
                onChange={(event) => setEditForm({ ...editForm, full_name: event.target.value })}
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Role
              <select
                value={editForm.role}
                onChange={(event) => setEditForm({ ...editForm, role: event.target.value })}
                style={styles.input}
              >
                <option value="">Not set</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.label}>
              Membership tier
              <select
                value={editForm.membership_tier}
                onChange={(event) => setEditForm({ ...editForm, membership_tier: event.target.value })}
                style={styles.input}
              >
                {TIER_OPTIONS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.label}>
              Organisation
              <input
                type="text"
                value={editForm.org_name}
                onChange={(event) => setEditForm({ ...editForm, org_name: event.target.value })}
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Home airfield
              <input
                type="text"
                value={editForm.home_airfield}
                onChange={(event) => setEditForm({ ...editForm, home_airfield: event.target.value.toUpperCase() })}
                style={styles.input}
              />
            </label>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={editForm.is_active}
                onChange={(event) => setEditForm({ ...editForm, is_active: event.target.checked })}
                style={styles.checkbox}
              />
              User is active
            </label>

            <div style={styles.modalActions}>
              <button type="button" style={styles.cancelButton} onClick={closeEdit} disabled={saving}>
                Cancel
              </button>
              <button type="button" style={styles.saveButton} onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '2rem',
    color: '#eaf6fb',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    margin: 0,
    color: '#1ecfff',
    fontSize: '2.2rem',
  },
  subtitle: {
    margin: '0.4rem 0 0',
    color: '#b6eaff',
  },
  backButton: {
    background: '#1ecfff',
    color: '#07111f',
    padding: '0.75rem 1rem',
    borderRadius: 10,
    textDecoration: 'none',
    fontWeight: 700,
    display: 'inline-block',
    marginTop: '1rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statBox: {
    background: '#181a2a',
    border: '1px solid rgba(30,207,255,0.25)',
    borderRadius: 16,
    padding: '1.2rem',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#ffffff',
  },
  statTitle: {
    color: '#b6eaff',
    marginTop: 6,
  },
  gridThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  card: {
    background: '#181a2a',
    border: '1px solid rgba(30,207,255,0.25)',
    borderRadius: 16,
    padding: '1.2rem',
    marginBottom: '1.5rem',
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: '0.35rem',
    color: '#1ecfff',
  },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '0.55rem 0',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  searchInput: {
    minWidth: 260,
    padding: '0.8rem 1rem',
    borderRadius: 10,
    border: '1px solid rgba(30,207,255,0.35)',
    background: '#0b1220',
    color: '#ffffff',
    fontSize: '1rem',
    outline: 'none',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 1120,
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    color: '#1ecfff',
    borderBottom: '1px solid rgba(255,255,255,0.16)',
    whiteSpace: 'nowrap',
  },
  td: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: '#eaf6fb',
    whiteSpace: 'nowrap',
  },
  muted: {
    color: '#b6eaff',
    margin: 0,
  },
  actionRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  smallButton: {
    background: '#1ecfff',
    color: '#07111f',
    border: 'none',
    borderRadius: 8,
    padding: '0.45rem 0.75rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  suspendButton: {
    background: '#ffb020',
    color: '#241500',
    border: 'none',
    borderRadius: 8,
    padding: '0.45rem 0.75rem',
    fontWeight: 800,
    cursor: 'pointer',
  },
  reactivateButton: {
    background: '#39d98a',
    color: '#062010',
    border: 'none',
    borderRadius: 8,
    padding: '0.45rem 0.75rem',
    fontWeight: 800,
    cursor: 'pointer',
  },
  activeBadge: {
    display: 'inline-block',
    background: '#12351f',
    color: '#d4ffe1',
    border: '1px solid #39d98a',
    borderRadius: 999,
    padding: '0.25rem 0.6rem',
    fontSize: '0.85rem',
    fontWeight: 800,
  },
  suspendedBadge: {
    display: 'inline-block',
    background: '#3b1111',
    color: '#ffd6d6',
    border: '1px solid #ff6b6b',
    borderRadius: 999,
    padding: '0.25rem 0.6rem',
    fontSize: '0.85rem',
    fontWeight: 800,
  },
  successBox: {
    background: '#12351f',
    color: '#d4ffe1',
    border: '1px solid #39d98a',
    borderRadius: 12,
    padding: '1rem',
    marginBottom: '1rem',
  },
  errorBox: {
    background: '#3b1111',
    color: '#ffd6d6',
    border: '1px solid #ff6b6b',
    borderRadius: 12,
    padding: '1rem',
    marginBottom: '1rem',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.72)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 9999,
  },
  modal: {
    width: '100%',
    maxWidth: 520,
    background: '#181a2a',
    border: '1px solid rgba(30,207,255,0.35)',
    borderRadius: 16,
    padding: '1.5rem',
    boxShadow: '0 12px 48px rgba(0,0,0,0.45)',
  },
  modalTitle: {
    marginTop: 0,
    color: '#1ecfff',
  },
  label: {
    display: 'block',
    color: '#b6eaff',
    fontWeight: 700,
    marginTop: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    color: '#b6eaff',
    fontWeight: 700,
    marginTop: '1rem',
  },
  checkbox: {
    width: 18,
    height: 18,
  },
  input: {
    width: '100%',
    marginTop: '0.4rem',
    padding: '0.75rem',
    borderRadius: 10,
    border: '1px solid rgba(30,207,255,0.35)',
    background: '#0b1220',
    color: '#ffffff',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  cancelButton: {
    background: 'transparent',
    color: '#b6eaff',
    border: '1px solid rgba(182,234,255,0.45)',
    borderRadius: 10,
    padding: '0.75rem 1rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  saveButton: {
    background: '#1ecfff',
    color: '#07111f',
    border: 'none',
    borderRadius: 10,
    padding: '0.75rem 1rem',
    fontWeight: 800,
    cursor: 'pointer',
  },
};